/**
 * App-wide global-search state (shares {@link useGlobalSearch}).
 */

import * as React from 'react';
import { createContext, useContext, ReactNode } from 'react';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';

/** Shared command-palette state from {@link useGlobalSearch}. */
type GlobalSearchContextType = ReturnType<typeof useGlobalSearch>;

/** Null until {@link GlobalSearchProvider} mounts. */
const GlobalSearchContext = createContext<GlobalSearchContextType | null>(null);

/** Owns one {@link useGlobalSearch} instance so Cmd+K and the navbar share the same query. */
export const GlobalSearchProvider: React.FC<{
  /** Tree that can read {@link useGlobalSearchContext}. */
  children: ReactNode;
}> = ({ children }) => {
  const searchState = useGlobalSearch();

  return (
    <GlobalSearchContext.Provider value={searchState}>{children}</GlobalSearchContext.Provider>
  );
};

/** Command-palette state; throws if used outside {@link GlobalSearchProvider}. */
export const useGlobalSearchContext = (): GlobalSearchContextType => {
  const context = useContext(GlobalSearchContext);
  if (!context) {
    throw new Error('useGlobalSearchContext must be used within GlobalSearchProvider');
  }
  return context;
};
